import { useState } from 'react';
import { identify, track } from '@fff-web/fff-analytics';
import { Compare } from '@fff-web/fff-utilities';
import { getCouponCode } from '../helpers';
import invalidateCaches from '../helpers/invalidateCaches';
import { getQueryParam } from '../pages/MyAccount/utils/url';
import { sendGTMReactivateConversion } from '../pages/ReactivateMembership/Events';
import { sendUpgradeCompletedEvent } from '../pages/Upgrade/Events';
import { reactivateExpiredSubscription, updateSubscription } from '../services/subscription';

const REACTIVATION_COMPLETED_EVENT = 'Reactivation Completed';
const REACTIVATION_COMPLETED_EVENT_TYPE = 'Expired Member';
const TRANSACTION_ERROR_CODE = 'TRANSACTION_ERROR';

const useShowErrorOrRedirectThankYouPage = (
  navigate,
  thankYouPagePath,
  currentPlan,
  couponsApplied = [],
) => {
  const [currentState, setCurrentState] = useState({
    showPayment: false, // response.status === 422
    errorPaymentMessage: '', // response.status === 422 errorPaymentMessage: body.customer_message
    showRetry: false, // response is not successful or is 422 but not a payment error or response is not json
    isLoading: false, // true once network call is started. False on errors. False when the onClose function is called
  });

  const retry = (error, response, sendEvent) => {
    console.error(error, response);
    sendEvent('failed', () => {
      setCurrentState({
        showPayment: false,
        errorPaymentMessage: '',
        showRetry: true,
        isLoading: false,
      });
    });
  };
  const onClose = () => {
    setCurrentState({
      showPayment: false,
      errorPaymentMessage: '',
      showRetry: false,
      isLoading: false,
    });
  };

  const handlePaySubscriptionResponse = (response, sendEvent, identifyProps = {}) => {
    if (response.ok) {
      sendEvent('success', async () => {
        // Await so both cache layers are busted before identify()/navigate read user info —
        // identify() calls getUserInfo, which would otherwise repopulate the client cache
        // from the not-yet-deleted BFF entry (stale state for up to the client TTL).
        await invalidateCaches();
        identify(identifyProps);
        if (Compare.isObject(thankYouPagePath)) {
          navigate(
            {
              ...thankYouPagePath,
            },
            { state: { coupon_code: getCouponCode(couponsApplied) } },
          );
        } else {
          navigate(thankYouPagePath, {
            state: { coupon_code: getCouponCode(couponsApplied) },
          });
        }
      });
    } else if (response.status === 422) {
      response
        .json()
        .then((body) => {
          if (body.errorCode === TRANSACTION_ERROR_CODE) {
            sendEvent('payment failed', () =>
              setCurrentState({
                showPayment: true,
                errorPaymentMessage: body.message,
                showRetry: false,
                isLoading: false,
              }),
            );
          } else {
            retry('It is not a transaction_error', response, sendEvent);
          }
        })
        .catch((error) => {
          retry(error, response, sendEvent);
        });
    } else {
      retry('It is not a 422', response, sendEvent);
    }
  };

  const sendSegmentEventAfterResponse = (ctaName, planSelected, status, callback) => {
    track({
      trackName: ctaName,
      properties: {
        plan_selected: planSelected,
        status,
        coupon_code: getCouponCode(couponsApplied),
      },
    })
      .then(callback)
      .catch((e) => {
        console.warn(e);
        callback();
      });
  };

  const sendReactivateSegmentEventAfterResponse = (status, callback) => {
    track({
      trackName: REACTIVATION_COMPLETED_EVENT,
      properties: {
        cta_name: 'Reactivate Now',
        type: REACTIVATION_COMPLETED_EVENT_TYPE,
        experience: getQueryParam('referrer'),
        reactivation_type: 'expired',
        status,
      },
    })
      .then(callback)
      .catch((e) => {
        console.warn(e);
        callback();
      });
  };

  const changeSubscription = (plan, captchaToken) => {
    setCurrentState({ ...currentState, isLoading: true });
    const sendUpdateLogsSegmentEvent = (status, callback) =>
      sendSegmentEventAfterResponse('Upgrade Logs', plan, status, callback);
    updateSubscription(plan, currentPlan, couponsApplied, captchaToken)
      .then((response) => {
        sendUpgradeCompletedEvent();
        handlePaySubscriptionResponse(response, sendUpdateLogsSegmentEvent, {
          traits: { planCode: plan },
        });
      })
      .catch((error) => retry(error, sendUpdateLogsSegmentEvent));
  };

  const changeReactivateSubscription = (plan, captchaToken) => {
    setCurrentState({ ...currentState, isLoading: true });
    const sendReactivateSegmentEvent = (status, callback) =>
      sendReactivateSegmentEventAfterResponse(status, callback);
    reactivateExpiredSubscription(plan, couponsApplied, captchaToken)
      .then((response) => {
        sendGTMReactivateConversion(response);
        handlePaySubscriptionResponse(response, sendReactivateSegmentEvent, {
          traits: { planCode: plan },
        });
      })
      .catch((error) => retry(error, sendReactivateSegmentEvent));
  };
  return {
    ...currentState,
    changeSubscription,
    onClose,
    changeReactivateSubscription,
  };
};

export default useShowErrorOrRedirectThankYouPage;
